'use client'

import {useCallback, useEffect, useRef, useState} from 'react'

export type LightboxImage = {full: string; alt: string; caption?: string | null}

/**
 * Shared, accessible lightbox gallery. Controlled by `openIndex` (null = closed).
 * Fade + scale animation, prev/next controls, ArrowLeft/Right keys, close via
 * button / click-outside / Escape, focus trap + restore, body scroll lock,
 * reduced-motion aware. Styles live in styles/testimonials.css (.lightbox*).
 */
export function Lightbox({
  images,
  openIndex,
  setOpenIndex,
}: {
  images: LightboxImage[]
  openIndex: number | null
  setOpenIndex: (i: number | null) => void
}) {
  const [show, setShow] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const restoreRef = useRef<HTMLElement | null>(null)

  const close = useCallback(() => {
    setShow(false)
    window.setTimeout(() => {
      setOpenIndex(null)
      restoreRef.current?.focus?.()
    }, 250)
  }, [setOpenIndex])

  const go = useCallback(
    (delta: number) => {
      if (openIndex === null || images.length === 0) return
      setOpenIndex((openIndex + delta + images.length) % images.length)
    },
    [openIndex, images.length, setOpenIndex],
  )

  useEffect(() => {
    if (openIndex === null) return

    restoreRef.current = (document.activeElement as HTMLElement) ?? null
    const raf = requestAnimationFrame(() => setShow(true))
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeBtnRef.current?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close()
      } else if (e.key === 'ArrowLeft') {
        go(-1)
      } else if (e.key === 'ArrowRight') {
        go(1)
      } else if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button')
        if (!focusable || focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [openIndex, close, go])

  if (openIndex === null) return null
  const current = images[openIndex]
  if (!current) return null

  return (
    <div
      ref={dialogRef}
      className={`lightbox${show ? ' open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      onClick={close}
    >
      <button
        ref={closeBtnRef}
        type="button"
        className="lightbox-btn lightbox-close"
        aria-label="Close gallery"
        onClick={close}
      >
        <CloseIcon />
      </button>

      {images.length > 1 && (
        <button
          type="button"
          className="lightbox-btn lightbox-prev"
          aria-label="Previous image"
          onClick={(e) => {
            e.stopPropagation()
            go(-1)
          }}
        >
          <ChevronIcon dir="left" />
        </button>
      )}

      <figure className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <img className="lightbox-img" src={current.full} alt={current.alt} />
        {(current.caption || images.length > 1) && (
          <figcaption className="lightbox-cap">
            {current.caption}
            {images.length > 1 && (
              <span className="lightbox-count">
                {openIndex + 1} / {images.length}
              </span>
            )}
          </figcaption>
        )}
      </figure>

      {images.length > 1 && (
        <button
          type="button"
          className="lightbox-btn lightbox-next"
          aria-label="Next image"
          onClick={(e) => {
            e.stopPropagation()
            go(1)
          }}
        >
          <ChevronIcon dir="right" />
        </button>
      )}
    </div>
  )
}

export function ZoomIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5L21 21M10.5 7.5v6M7.5 10.5h6" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

function ChevronIcon({dir}: {dir: 'left' | 'right'}) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir === 'left' ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
    </svg>
  )
}
